import { useEffect, useRef } from 'react'

/**
 * Re-run `onFocus` whenever the tab regains focus or becomes visible again, so a page left open
 * reflects state that changed elsewhere (e.g. a coaching relationship activating once the guardian
 * signs) without a manual reload. Does not fire on mount — the page loads its own data — and coalesces
 * the window `focus` and document `visibilitychange` events. Callers should make their refetch a no-op
 * (or guard) while a load is already in flight.
 *
 * @param {() => void} onFocus - called when the tab becomes visible/focused again
 * @param {boolean} [enabled=true] - disable to skip attaching listeners
 */
export function useRefetchOnFocus(onFocus, enabled = true) {
  const cb = useRef(onFocus)
  cb.current = onFocus

  useEffect(() => {
    if (!enabled) return undefined
    const handler = () => {
      if (document.visibilityState === 'visible') cb.current?.()
    }
    window.addEventListener('focus', handler)
    document.addEventListener('visibilitychange', handler)
    return () => {
      window.removeEventListener('focus', handler)
      document.removeEventListener('visibilitychange', handler)
    }
  }, [enabled])
}

export default useRefetchOnFocus
